import { getSystem } from '@/api/system/set';
import { Login, initPassword, getInitInfo } from '@/api';
import { Button, Form, Input, message } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setUser } from '@/utils/auth';
import './style.less';

// 默认插画已移除，背景图直接从 systemData.system_background_image 读取

const resolveAssetUrl = (value?: string) => {
  if (!value) return '';
  // 对路径中的中文及特殊字符进行编码（保留路径分隔符和已编码的内容）
  const encodePath = (p: string) => p.split('/').map((seg) => (/[^\x00-\x7F]/.test(seg) ? encodeURIComponent(seg) : seg)).join('/');
  if (/^https?:\/\//i.test(value)) return encodePath(value);
  if (value.startsWith('//')) return encodePath(`${window.location.protocol}${value}`);
  if (value.startsWith('/')) return `${window.location.origin}${encodePath(value)}`;
  // 处理 host/path 格式（如 123.57.217.68/iot-bucket/...）
  if (/^[\w.-]+(?::\d+)?\//.test(value)) return encodePath(`${window.location.protocol}//${value}`);
  return `${window.location.origin}/${encodePath(value.replace(/^\/+/, ''))}`;
};

export default function LoginPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [initInfo, setInitInfo] = useState<{ data: { isInit: boolean } }>({ data: { isInit: true } });
  const [systemData, setSystemData] = useState<Record<string, any>>({});

  useEffect(() => {
    // 获取是否需要初始化密码
    getInitInfo<{ isInit: boolean }>()
      .then((res) => {
        if (res?.result !== undefined) {
          setInitInfo({ data: { isInit: res.result.isInit ?? true } });
        }
      })
      .catch(() => {});
    const cache = window.localStorage.getItem('$systemData$');
    if (cache) {
      try {
        setSystemData(JSON.parse(cache) || {});
      } catch {
        window.localStorage.removeItem('$systemData$');
      }
    }
    getSystem({})
      .then((res) => {
        const nextData = res?.result || {};
        setSystemData(nextData);
        window.localStorage.setItem('$systemData$', JSON.stringify(nextData));
      })
      .catch(() => {});
  }, []);

  const loginSuccess = useCallback(() => {
    const path = search.get('path') || '/home/dashboard';
    navigate(path);
  }, [navigate, search]);

  const onFinish = (values: any) => {
    Login(values).then((resp) => {
      if (resp.success) {
        setUser(resp.result);
        loginSuccess();
      } else {
        message.error(resp.errorMsg);
      }
    });
  };

  const onInitFinish = (values: any) => {
    initPassword({ ...values, password1: undefined }).then((resp) => {
      if (resp.success) {
        message.success('初始化密码成功');
      } else {
        message.error(resp.errorMsg);
      }
    });
  };

  const logoUrl = useMemo(() => {
    return resolveAssetUrl(systemData?.system_logo_file);
  }, [systemData]);

  return (
    <div className="premium-login-container">
      <div className="login-glass-card">
        {/* 左侧：品牌展示区 */}
        <div className="login-brand-side">
          <div className="brand-content">
            <h1 className="fade-in-up">Welcome!</h1>
            <p className="fade-in-up-delay-1">{systemData?.system_name || '物联网平台'}</p>
            {systemData?.system_background_image ? (
              <div className="brand-bg-preview fade-in-scale">
                <img src={resolveAssetUrl(systemData.system_background_image)} alt="登录背景图" />
              </div>
            ) : (
              <div className="brand-bg-preview brand-bg-placeholder fade-in-scale">
                <svg viewBox="0 0 240 160" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="240" height="160" rx="8" fill="rgba(255,255,255,0.04)"/>
                  <circle cx="120" cy="70" r="30" stroke="rgba(255,255,255,0.15)" strokeWidth="2"/>
                  <path d="M90 70 Q120 40 150 70 Q120 100 90 70Z" fill="rgba(255,255,255,0.08)"/>
                  <text x="120" y="120" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="12">在系统设置中上传背景图</text>
                </svg>
              </div>
            )}
          </div>

          {/* S 型曲线 SVG 遮罩 */}
          <div className="s-curve-divider">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none">
              <path d="M100,0 C65,0 35,100 0,100 L100,100 Z" fill="white" />
            </svg>
          </div>
        </div>

        {/* 右侧：表单区 */}
        <div className="login-form-side">
          <div className="form-wrapper">
            <header className="form-header fade-in-up">
              <h2>{initInfo.data?.isInit ? '账号登录' : '初始化密码'}</h2>
              <i className="accent-bar"></i>
            </header>

            <Form
              name="login"
              onFinish={initInfo.data?.isInit ? onFinish : onInitFinish}
              layout="vertical"
              className="elegant-form fade-in-up-delay-2"
            >
              {initInfo.data?.isInit ? (
                <>
                  <Form.Item name="username" rules={[{ required: true, message: '请输入账号' }]}>
                    <Input placeholder="请输入手机号码 / 邮箱" variant="borderless" className="modern-input" />
                  </Form.Item>
                  <Form.Item name="password" rules={[{ required: true, message: '请输入密码' }]}>
                    <Input.Password placeholder="请输入密码" variant="borderless" className="modern-input" />
                  </Form.Item>
                </>
              ) : (
                <>
                  <Form.Item name="newPassword" rules={[{ required: true, message: '请输入新密码' }]}>
                    <Input.Password placeholder="请输入新密码" variant="borderless" className="modern-input" />
                  </Form.Item>
                  <Form.Item
                    name="password1"
                    dependencies={['newPassword']}
                    rules={[
                      { required: true, message: '请再次输入密码' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('newPassword') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('两次密码输入不一致'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password placeholder="请再次输入新密码" variant="borderless" className="modern-input" />
                  </Form.Item>
                </>
              )}

              <Form.Item className="submit-item">
                <Button type="primary" htmlType="submit" block className="pill-submit-btn">
                  {initInfo.data?.isInit ? '立即登录' : '确认初始化'}
                </Button>
              </Form.Item>
            </Form>
            
            {logoUrl && <img src={logoUrl} className="subtle-footer-logo" alt="logo" />}
          </div>
        </div>
      </div>

      <footer className="global-footer">
        Copyright © {new Date().getFullYear()} {systemData?.copyright?.replace('蜂鸟', '') || '物联网平台'}
        {systemData?.version && <span> | v{systemData.version}</span>}
      </footer>
    </div>
  );
}